import React, { useState, useEffect, useCallback, useRef } from 'react';
import { eventPersonLabel, eventPersonTypeLabel } from '../utils/eventPersonLabel';
import { getUnregisteredTagInfo } from '../utils/eventHelpers';
import {
  Activity,
  Search,
  X,
  ChevronDown,
  ChevronLeft,
  User,
  Car,
  Wifi,
  Tag,
  Clock,
  MapPin,
  Home,
  DoorOpen,
  DoorClosed,
  Check,
  Building2,
  Loader2,
  Calendar,
  Settings,
  Fingerprint,
  Camera,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ReportButton } from '../components/ui/ReportButton';
import { useCustomer } from '../contexts/CustomerContext';
import { usePersonDetail } from '../hooks/usePersonDetail';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import { fetchWithAuth } from '../services/api';
import Pagination from '../components/Pagination';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { cn } from '../utils/cn';
import { LicensePlate } from '../components/ui/LicensePlate';
import { useForceUppercase } from '../hooks/useForceUppercase';
import { usePorteiroReports } from '../hooks/usePorteiroReports';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import { getAbsoluteImageUrl } from '../utils/imageUrl';

registerLocale('pt-BR', ptBR);

// ─── Types ────────────────────────────────────────────────────────────────────

interface AccessEvent {
  id: string;
  person_id?: string;
  person_name?: string;
  person_type?: string;
  event_type?: string;
  direction?: string;
  zone_name?: string;
  device_name?: string;
  event_timestamp: string;
  is_authorized?: boolean;
  status?: string;
  authorization_decision?: string;
  not_registered_in_system?: boolean;
  department_name?: string;
  unit_name?: string;
  face_photo_url?: string;
  identity_photo_url?: string;
  vehicle_plate?: string;
  vehicle_id?: string;
  unit_id?: string;
  access_type?: string; // 'rfid' for vehicle/tag events
}

interface Zone {
  id: string;
  name: string;
}

interface Department {
  id: string;
  name: string;
}

interface Block {
  id: string;
  name: string;
}

interface UnitItem {
  id: string;
  number: string;
  block_id: string;
}

interface TimeSpentEntry {
  person_id?: string;
  person_name: string;
  person_type?: string;
  face_photo_url?: string;
  unit_name?: string;
  department_name?: string;
  visits_count: number;
  total_seconds: number;
  avg_seconds_per_visit: number;
  last_entry?: string;
  last_exit?: string;
  currently_inside: boolean;
}

interface EventosGeraisProps {
  onBack?: () => void;
  customerId?: string;
  customerType?: 'condominio' | 'empresa';
  timeSpentEnabled?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// The category map moved to utils/eventPersonLabel, shared with the operational panel — which
// also PREFERS the position the backend resolved (`person_role`: Proprietário, Inquilino,
// Responsável). This page only ever showed the category, so the same event read differently on
// the two screens, and an empresa member always read "Funcionário".
const formatPersonType = (pt?: string) => eventPersonTypeLabel(pt);

const SYSTEM_EVENT_TYPES = new Set([
  'door_opened', 'door_closed', 'door_open_timeout', 'exit_button_pressed',
  'alarm_triggered', 'system_event',
]);

const isRemoteTrigger = (eventType?: string) =>
  eventType === 'remote_door_trigger' || eventType === 'remote_user';

const EVENT_TYPE_LABELS: Record<string, string> = {
  authentication_success: 'Autenticação', door_opened: 'Porta Aberta',
  door_open_timeout: 'Alerta de Porta', door_closed: 'Porta Fechada',
  exit_button_pressed: 'Botão de Saída', alarm_triggered: 'Alarme',
  system_event: 'Evento de Sistema', remote_user: 'Abertura Remota',
  remote_door_trigger: 'Abertura Remota',
  lpr_access: 'Acesso LPR',
};

const getDisplayName = (event: AccessEvent) =>
  event.person_name || EVENT_TYPE_LABELS[event.event_type ?? ''] || event.event_type || '—';

const isSystemEvt = (eventType?: string) => SYSTEM_EVENT_TYPES.has(eventType ?? '');

const getDirectionInfo = (direction?: string, eventType?: string) => {
  if (isRemoteTrigger(eventType))  return { label: 'Abertura Remota', bg: 'bg-indigo-100', text: 'text-indigo-700', darkBg: 'bg-indigo-500/20', darkText: 'text-indigo-400', icon: <Fingerprint  className="h-3 w-3" /> };
  if (direction === 'saida')       return { label: 'Saída',           bg: 'bg-blue-100',   text: 'text-blue-700',   darkBg: 'bg-blue-500/20',   darkText: 'text-blue-400',   icon: <DoorClosed   className="h-3 w-3" /> };
  if (direction === 'passagem')    return { label: 'Passagem',        bg: 'bg-blue-100', text: 'text-blue-700', darkBg: 'bg-blue-500/20', darkText: 'text-blue-400', icon: <DoorOpen     className="h-3 w-3" /> };
  if (direction === 'entrada')     return { label: 'Entrada',         bg: 'bg-green-100',  text: 'text-green-700',  darkBg: 'bg-green-500/20',  darkText: 'text-green-400',  icon: <DoorOpen     className="h-3 w-3" /> };
  if (isSystemEvt(eventType))      return { label: 'Sistema',         bg: 'bg-gray-100',   text: 'text-gray-600',   darkBg: 'bg-gray-500/20',   darkText: 'text-gray-400',   icon: <Settings     className="h-3 w-3" /> };
  return                                  { label: 'Evento',          bg: 'bg-gray-100',   text: 'text-gray-600',   darkBg: 'bg-gray-500/20',   darkText: 'text-gray-400',   icon: <Check        className="h-3 w-3" /> };
};

const getBorderColor = (direction?: string, eventType?: string) => {
  if (isRemoteTrigger(eventType)) return 'border-l-indigo-500';
  if (direction === 'saida')      return 'border-l-blue-400';
  if (direction === 'passagem')   return 'border-l-blue-400';
  if (direction === 'entrada')    return 'border-l-green-500';
  if (isSystemEvt(eventType))     return 'border-l-gray-400';
  return 'border-l-gray-300';
};

const formatTimestamp = (ts: string) => {
  try {
    return new Date(ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
};

const formatDuration = (s: number) => {
  if (s <= 0) return '0s';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const PERSON_TYPE_OPTIONS_CONDO = [
  { value: 'all',              label: 'Todos os tipos' },
  { value: 'resident',         label: 'Morador' },
  { value: 'visitor',          label: 'Visitante' },
  { value: 'service_provider', label: 'Prestador' },
];

const PERSON_TYPE_OPTIONS_EMPRESA = [
  { value: 'all',              label: 'Todos os tipos' },
  { value: 'visitor',          label: 'Visitante' },
  { value: 'service_provider', label: 'Prestador' },
  { value: 'staff',            label: 'Funcionário' },
];

const DIRECTION_OPTIONS = [
  { value: 'all',      label: 'Todas as direções' },
  { value: 'entrada',  label: 'Entrada' },
  { value: 'saida',    label: 'Saída' },
  { value: 'passagem', label: 'Passagem' },
];

const PAGE_SIZE = 25;

// ─── Custom Select (dark, for embedded view) ──────────────────────────────────

const DarkSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  label: string;
}> = ({ value, onChange, options, label }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const current = options.find(o => o.value === value)?.label ?? label;
  return (
    <div ref={ref} className="relative">
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <button onClick={() => setOpen(o => !o)} className="flex items-center justify-between gap-2 w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 hover:border-gray-500 transition-colors whitespace-nowrap min-w-[140px]">
        <span className={value === 'all' ? 'text-gray-400' : 'text-gray-100'}>{current}</span>
        <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl min-w-full">
          {options.map(opt => (
            <button key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
              className={cn("w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg", value === opt.value ? "text-cyan-400 bg-gray-700/50" : "text-gray-300")}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Light Select (for standalone page) ──────────────────────────────────────

const LightSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}> = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const current = options.find(o => o.value === value)?.label ?? placeholder;
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} className="flex items-center justify-between gap-1.5 px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg text-gray-700 hover:border-gray-300 transition-colors whitespace-nowrap min-w-[120px]">
        <span className={value === 'all' ? 'text-gray-400' : 'text-gray-800 font-medium'}>{current}</span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg min-w-full overflow-hidden">
          {options.map(opt => (
            <button key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
              className={cn("w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors", value === opt.value ? "text-gray-900 font-semibold bg-gray-50" : "text-gray-600")}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Searchable Select (dark + light, with built-in search input) ─────────────

const SearchableSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  dark?: boolean;
  disabled?: boolean;
  label?: string; // used in dark mode
}> = ({ value, onChange, options, placeholder, dark = false, disabled = false, label }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  useEffect(() => {
    if (open) { setSearch(''); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  const filtered = options.filter(o =>
    o.value === 'all' || o.label.toLowerCase().includes(search.toLowerCase())
  );
  const current = options.find(o => o.value === value)?.label ?? placeholder;

  if (dark) {
    return (
      <div ref={ref} className="relative">
        {label && <label className="block text-xs text-gray-400 mb-1">{label}</label>}
        <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)}
          className="flex items-center justify-between gap-2 px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 hover:border-gray-500 transition-colors whitespace-nowrap min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed">
          <span className={value === 'all' ? 'text-gray-400' : 'text-gray-100'}>{current}</span>
          <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-1 z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl w-56">
            <div className="p-2 border-b border-gray-700">
              <input ref={inputRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar..." className="w-full px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyan-500" />
            </div>
            <div className="max-h-48 overflow-y-scroll [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-700/40 [&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-thumb]:rounded-full">
              {filtered.map(opt => (
                <button key={opt.value} type="button" onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={cn("w-full text-left px-3 py-2 text-sm hover:bg-gray-700 transition-colors truncate", value === opt.value ? "text-cyan-400 bg-gray-700/50" : "text-gray-300")}>
                  {opt.label}
                </button>
              ))}
              {filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-500">Nenhum resultado</div>}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between gap-1.5 px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg text-gray-700 hover:border-gray-300 transition-colors whitespace-nowrap min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed">
        <span className={value === 'all' ? 'text-gray-400' : 'text-gray-800 font-medium'}>{current}</span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg w-56 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input ref={inputRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..." className="w-full px-2 py-1 text-xs border border-gray-200 rounded text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300" />
          </div>
          <div className="max-h-48 overflow-y-scroll [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
            {filtered.map(opt => (
              <button key={opt.value} type="button" onClick={() => { onChange(opt.value); setOpen(false); }}
                className={cn("w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors truncate", value === opt.value ? "text-gray-900 font-semibold bg-gray-50" : "text-gray-600")}>
                {opt.label}
              </button>
            ))}
            {filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">Nenhum resultado</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Event Row (light, for standalone page) ───────────────────────────────────

/**
 * A plate read whose vehicle belongs to a person wears that person's face, exactly as in the
 * operational sidebar (see EventsPanel.EventCard). The plate stays where it is — it is the
 * credential that was actually read, and it is what an operator matches against the gate.
 *
 * `face_photo_url`/`identity_photo_url` are only ever filled for an event carrying a person_id,
 * and the LPR handlers set person_id solely from `vehicles.person_id`, so a photo on a vehicle
 * event means exactly "owned directly by a person". A car held by the unit, or an unregistered
 * plate, has none and keeps the glyph — an empty frame reads worse.
 */
export const vehicleEventOwnerPhoto = (
  photoUrl: string | undefined,
  isVehicleEvent: boolean,
): string | null => (isVehicleEvent && photoUrl ? photoUrl : null);

export const LightEventRow: React.FC<{ event: AccessEvent; onClick: (e: AccessEvent) => void }> = ({ event, onClick }) => {
  const forceUpper = useForceUppercase();
  const name = getDisplayName(event);
  const dir = getDirectionInfo(event.direction, event.event_type);
  const borderColor = getBorderColor(event.direction, event.event_type);
  const photoUrl = event.face_photo_url ?? event.identity_photo_url;
  const location = event.zone_name
    ? event.device_name ? `${event.zone_name} (${event.device_name})` : event.zone_name
    : event.device_name ?? null;
  const locationMeta = event.unit_name || event.department_name || null;
  // LPR events are always "vehicle-like" — Camera icon, no typeLabel chip
  const isLPREvent = event.event_type === 'lpr_access';
  const isVehicleEvent = !!event.vehicle_plate || isLPREvent;
  const ownerPhoto = vehicleEventOwnerPhoto(photoUrl, isVehicleEvent);
  // Guarita sends no name (event-processor rewrites it to "Tag não cadastrada"), so a
  // person_name digit test matched ControlID only. getUnregisteredTagInfo uses the
  // not_registered_in_system flag and reads the number from employee_no, which both send.
  const { isUnregistered: isUnknownTag, tagValue: unknownTagValue } = getUnregisteredTagInfo(event as any);
  const typeLabel = (isVehicleEvent || isUnknownTag) ? null : eventPersonLabel(event);
  const clickable = !!event.person_id || isVehicleEvent;

  return (
    <li
      onClick={() => { if (clickable) onClick(event); }}
      className={cn(
        "flex items-center gap-3 px-4 py-2.5 border-l-4",
        borderColor,
        clickable ? "cursor-pointer hover:bg-gray-50" : "bg-white"
      )}
    >
      {/* Avatar */}
      {ownerPhoto ? (
        <div className="flex-shrink-0 w-9 h-9 rounded-md overflow-hidden border border-blue-200 relative">
          <img data-testid="event-owner-photo" src={ownerPhoto} alt="" className="h-full w-full object-cover" />
          {/* The face says WHO, the badge says by WHAT they came through. */}
          <span
            data-testid="event-vehicle-badge"
            title="Passagem de veículo"
            className="absolute bottom-0 right-0 rounded-full bg-blue-500 p-0.5 ring-1 ring-white flex items-center justify-center"
          >
            {isLPREvent ? <Camera className="h-2 w-2 text-white" /> : <Car className="h-2 w-2 text-white" />}
          </span>
        </div>
      ) : isVehicleEvent ? (
        <div data-testid="event-vehicle-glyph" className="flex-shrink-0 w-9 h-9 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
          {event.event_type === 'lpr_access'
            ? <Camera className="h-4 w-4 text-blue-500" />
            : <Car className="h-4 w-4 text-blue-500" />
          }
        </div>
      ) : isUnknownTag ? (
        <div className="flex-shrink-0 w-9 h-9 rounded-md bg-violet-50 border border-violet-200 flex items-center justify-center">
          <Wifi className="h-4 w-4 text-violet-500" />
        </div>
      ) : event.event_type === 'lpr_access' ? (
        <div className="flex-shrink-0 w-9 h-9 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
          <Camera className="h-4 w-4 text-blue-500" />
        </div>
      ) : (
        <div className={cn(
          "flex-shrink-0 w-7 h-9 rounded-md overflow-hidden border flex items-center justify-center",
          isRemoteTrigger(event.event_type) ? "bg-indigo-50 border-indigo-200" : "bg-gray-100 border-gray-200"
        )}>
          {photoUrl
            ? <img data-testid="event-owner-photo" src={photoUrl} alt="" className="h-full w-full object-cover" />
            : isRemoteTrigger(event.event_type)
              ? <Fingerprint className="h-4 w-4 text-indigo-500" />
              : isSystemEvt(event.event_type)
                ? <Settings className="h-4 w-4 text-gray-400" />
                : <User className="h-4 w-4 text-gray-400" />
          }
        </div>
      )}

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-nowrap min-w-0">
          {isVehicleEvent ? (
            event.vehicle_plate
              ? <LicensePlate plate={event.vehicle_plate} size="sm" />
              : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-semibold text-blue-700">Acesso LPR</span>
          ) : isUnknownTag ? (
            <>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[11px] font-bold bg-gray-900 border-gray-600 text-gray-100 tracking-wider max-w-[120px] flex-shrink min-w-0">
                <span className="truncate">{unknownTagValue || '—'}</span>
              </span>
              <span className="whitespace-nowrap flex-shrink-0 text-[10px] font-medium text-gray-500">
                Tag não cadastrada
              </span>
            </>
          ) : (
            <span style={forceUpper ? { textTransform: 'uppercase' } : undefined} className={cn("text-sm font-medium truncate", clickable ? "text-gray-900" : "text-gray-600")}>{name}</span>
          )}
          {typeLabel && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded border border-gray-200">{typeLabel}</span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          {isVehicleEvent && name && (
            <span style={forceUpper ? { textTransform: 'uppercase' } : undefined} className="flex items-center gap-1 text-xs text-gray-500 truncate">
              <User className="h-3 w-3 flex-shrink-0" />{name}
            </span>
          )}
          {locationMeta && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Home className="h-3 w-3" />{locationMeta}
            </span>
          )}
          {location && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <MapPin className="h-3 w-3" />{location}
            </span>
          )}
        </div>
      </div>

      {/* Right side: time + direction */}
      <div className="flex-shrink-0 flex flex-col items-end gap-1">
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <Clock className="h-3 w-3" />
          <span>{formatTimestamp(event.event_timestamp)}</span>
        </div>
        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold", dir.bg, dir.text)}>
          {dir.icon}{dir.label}
        </span>
      </div>
    </li>
  );
};

// ─── Event Card (dark, for embedded view) ────────────────────────────────────

export const DarkEventCard: React.FC<{ event: AccessEvent; onClick: (e: AccessEvent) => void }> = ({ event, onClick }) => {
  const name = getDisplayName(event);
  const dir = getDirectionInfo(event.direction, event.event_type);
  const borderColor = getBorderColor(event.direction, event.event_type);
  const photoUrl = event.face_photo_url ?? event.identity_photo_url;
  const location = event.zone_name
    ? event.device_name ? `${event.zone_name} (${event.device_name})` : event.zone_name
    : event.device_name ?? null;
  const isLPREvent = event.event_type === 'lpr_access';
  const isVehicleEvent = !!event.vehicle_plate || isLPREvent;
  const ownerPhoto = vehicleEventOwnerPhoto(photoUrl, isVehicleEvent);
  // Guarita sends no name (event-processor rewrites it to "Tag não cadastrada"), so a
  // person_name digit test matched ControlID only. getUnregisteredTagInfo uses the
  // not_registered_in_system flag and reads the number from employee_no, which both send.
  const { isUnregistered: isUnknownTag, tagValue: unknownTagValue } = getUnregisteredTagInfo(event as any);
  const typeLabel = (isVehicleEvent || isUnknownTag) ? null : eventPersonLabel(event);
  const clickable = !!event.person_id || isVehicleEvent;

  return (
    <div onClick={() => { if (clickable) onClick(event); }}
      className={cn("bg-gray-900 rounded-lg border border-gray-800 border-l-4 overflow-hidden flex min-h-[72px]", borderColor, clickable && "cursor-pointer hover:bg-gray-800/60 hover:border-gray-600 transition-all")}>
      <div className="w-14 flex-shrink-0 relative">
        {ownerPhoto ? (
          <>
            <img data-testid="event-owner-photo" src={ownerPhoto} alt="" className="absolute inset-0 w-full h-full object-cover" />
            {/* The face says WHO, the badge says by WHAT they came through. */}
            <span
              data-testid="event-vehicle-badge"
              title="Passagem de veículo"
              className="absolute bottom-0.5 right-0.5 rounded-full bg-blue-500 p-1 ring-2 ring-gray-900 flex items-center justify-center"
            >
              {isLPREvent ? <Camera className="h-2.5 w-2.5 text-white" /> : <Car className="h-2.5 w-2.5 text-white" />}
            </span>
          </>
        ) : isVehicleEvent ? (
          <div data-testid="event-vehicle-glyph" className="absolute inset-0 flex items-center justify-center bg-blue-500/15">
            {event.event_type === 'lpr_access'
              ? <Camera className="h-6 w-6 text-blue-400" />
              : <Car className="h-6 w-6 text-blue-400" />
            }
          </div>
        ) : isUnknownTag ? (
          <div className="absolute inset-0 flex items-center justify-center bg-violet-500/15">
            <Wifi className="h-6 w-6 text-violet-400" />
          </div>
        ) : event.event_type === 'lpr_access' ? (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-500/15">
            <Camera className="h-6 w-6 text-blue-400" />
          </div>
        ) : photoUrl ? (
          <img data-testid="event-owner-photo" src={photoUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : isRemoteTrigger(event.event_type) ? (
          <div className="absolute inset-0 flex items-center justify-center bg-indigo-500/15"><Fingerprint className="h-6 w-6 text-indigo-400" /></div>
        ) : (
          <div className={cn("absolute inset-0 flex items-center justify-center", dir.darkBg)}><User className="h-6 w-6 text-gray-400" /></div>
        )}
      </div>
      <div className="flex-1 min-w-0 p-2.5">
        {isVehicleEvent ? (
          <>
            {event.vehicle_plate
              ? <LicensePlate plate={event.vehicle_plate} size="sm" />
              : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-500/20 border border-blue-500/30 text-[10px] font-semibold text-blue-400">Acesso LPR</span>
            }
            {name && <p className="text-xs text-gray-400 truncate mt-0.5 flex items-center gap-1"><User className="h-2.5 w-2.5 flex-shrink-0" />{name}</p>}
          </>
        ) : isUnknownTag ? (
          <div className="flex items-center gap-1 flex-nowrap min-w-0">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-mono text-[10px] font-bold bg-gray-900 border-gray-600 text-gray-100 tracking-wider max-w-[90px] flex-shrink min-w-0">
              <span className="truncate">{unknownTagValue || '—'}</span>
            </span>
            <span className="whitespace-nowrap flex-shrink-0 text-[9px] font-medium text-gray-500">
              Tag não cadastrada
            </span>
          </div>
        ) : (
          <p className="text-sm font-medium text-gray-100 truncate">{name}</p>
        )}
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {typeLabel && <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-700 text-gray-300 rounded">{typeLabel}</span>}
          {event.unit_name && <span className="flex items-center gap-1 text-[10px] text-gray-400"><Home className="h-2.5 w-2.5" />{event.unit_name}</span>}
          {event.department_name && !event.unit_name && <span className="flex items-center gap-1 text-[10px] text-gray-400"><Building2 className="h-2.5 w-2.5" />{event.department_name}</span>}
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="flex items-center gap-1 text-xs text-gray-300"><Clock className="h-3 w-3 text-gray-400" />{formatTimestamp(event.event_timestamp)}</span>
          <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold", dir.darkBg, dir.darkText)}>{dir.icon}{dir.label}</span>
        </div>
        {location && <div className="flex items-center gap-1 mt-1 text-xs text-gray-400"><MapPin className="h-3 w-3 flex-shrink-0" /><span className="truncate">{location}</span></div>}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const EventosGerais: React.FC<EventosGeraisProps> = ({ onBack, customerId: customerIdProp, customerType: customerTypeProp, timeSpentEnabled: timeSpentEnabledProp }) => {
  const navigate = useNavigate();
  const { selectedCustomer } = useCustomer();
  const { openPersonDetail } = usePersonDetail();
  const { onUnitClick } = usePersonDetailContext();
  const canViewReports = usePorteiroReports();
  const embedded = !!onBack;

  const customerId = customerIdProp ?? selectedCustomer?.id ?? null;
  const customerType = customerTypeProp ?? (selectedCustomer?.type as 'condominio' | 'empresa' | undefined) ?? 'condominio';
  const condoType = (selectedCustomer as any)?.condoType ?? 'apartamento';
  const blockLabel = condoType === 'casas' ? 'Quadra' : 'Bloco';
  const unitLabel = condoType === 'casas' ? 'Lote' : 'Unidade';

  const [timeSpentEnabled, setTimeSpentEnabled] = useState<boolean>(timeSpentEnabledProp ?? false);
  const personTypeOptions = customerType === 'empresa' ? PERSON_TYPE_OPTIONS_EMPRESA : PERSON_TYPE_OPTIONS_CONDO;

  // Fetch customer settings to check time_spent_enabled (context doesn't carry condo_data)
  useEffect(() => {
    if (timeSpentEnabledProp !== undefined || !customerId || embedded) return;
    fetchWithAuth(`/api/v1/customers/${customerId}/settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          const enabled = !!(d.condo_data?.time_spent_enabled || d.empresa_data?.time_spent_enabled);
          setTimeSpentEnabled(enabled);
        }
      })
      .catch(() => {});
  }, [customerId, embedded, timeSpentEnabledProp]);

  const [activeTab, setActiveTab] = useState<'events' | 'time-spent'>('events');
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);

  // Location filter state (empresa: department; condo: block + unit)
  const [departments, setDepartments] = useState<Department[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [units, setUnits] = useState<UnitItem[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [blockFilter, setBlockFilter] = useState('all');
  const [unitFilter, setUnitFilter] = useState('all');

  // Time-spent tab state
  const [timeSpentData, setTimeSpentData] = useState<TimeSpentEntry[]>([]);
  const [timeSpentLoading, setTimeSpentLoading] = useState(false);
  const [insideOnly, setInsideOnly] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  const fetchZones = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await fetchWithAuth(`/api/v1/zones/customer/${customerId}`);
      if (res.ok) { const d = await res.json(); setZones(d.zones || []); }
    } catch {}
  }, [customerId]);

  // Fetch departments (empresa) or blocks (condo) for location filter
  useEffect(() => {
    if (!customerId) return;
    if (customerType === 'empresa') {
      fetchWithAuth(`/api/v1/customers/${customerId}/departments`)
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d) { const raw = d.data ?? d.departments ?? d; setDepartments(Array.isArray(raw) ? raw : []); }
        })
        .catch(() => {});
    } else {
      fetchWithAuth(`/api/v1/customers/${customerId}/blocks`)
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (d) setBlocks(Array.isArray(d) ? d : (d.data ?? d.blocks ?? []));
        })
        .catch(() => {});
    }
  }, [customerId, customerType]);

  // Fetch units when a block is selected
  useEffect(() => {
    if (blockFilter === 'all') { setUnits([]); setUnitFilter('all'); return; }
    fetchWithAuth(`/api/v1/blocks/${blockFilter}/units`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setUnits(Array.isArray(d) ? d : (d.data ?? []));
      })
      .catch(() => {});
  }, [blockFilter]);

  const fetchEvents = useCallback(async (page: number) => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ customer_id: customerId, limit: PAGE_SIZE.toString(), page: page.toString() });
      if (zoneFilter !== 'all') params.append('zone_id', zoneFilter);
      if (directionFilter !== 'all') params.append('direction', directionFilter);
      if (typeFilter !== 'all') params.append('person_type', typeFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) { const e = new Date(endDate); e.setHours(23, 59, 59, 999); params.append('end_date', e.toISOString()); }
      if (unitFilter !== 'all') params.append('unit_id', unitFilter);
      if (departmentFilter !== 'all') params.append('department_id', departmentFilter);
      const res = await fetchWithAuth(`/api/v1/events/access?${params}`);
      if (res.ok) { const d = await res.json(); setEvents(d.data || []); setTotalEvents(d.total || 0); }
    } catch {} finally { setIsLoading(false); }
  }, [customerId, zoneFilter, directionFilter, typeFilter, debouncedSearch, startDate, endDate, unitFilter, departmentFilter]);

  // Fetches events matching the current filters (not just the visible page) for
  // export — same filter params as fetchEvents above, but a single request instead of
  // paginating. Capped at EXPORT_LIMIT: an unbounded export (e.g. no date range set on
  // a busy customer) both hammers the API and freezes the tab rendering a huge PDF
  // table client-side, so we cap the request and warn if there's more than that.
  const EXPORT_LIMIT = 500;
  const fetchEventsForExport = async (): Promise<{ events: AccessEvent[]; total: number }> => {
    if (!customerId) return { events: [], total: 0 };
    const params = new URLSearchParams({ customer_id: customerId, limit: EXPORT_LIMIT.toString(), page: '1' });
    if (zoneFilter !== 'all') params.append('zone_id', zoneFilter);
    if (directionFilter !== 'all') params.append('direction', directionFilter);
    if (typeFilter !== 'all') params.append('person_type', typeFilter);
    if (debouncedSearch) params.append('search', debouncedSearch);
    if (startDate) params.append('start_date', startDate.toISOString());
    if (endDate) { const e = new Date(endDate); e.setHours(23, 59, 59, 999); params.append('end_date', e.toISOString()); }
    if (unitFilter !== 'all') params.append('unit_id', unitFilter);
    if (departmentFilter !== 'all') params.append('department_id', departmentFilter);
    const res = await fetchWithAuth(`/api/v1/events/access?${params}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const d = await res.json();
    return { events: d.data || [], total: d.total ?? (d.data || []).length };
  };

  const buildExportRow = (ev: AccessEvent) => {
    const dir = getDirectionInfo(ev.direction, ev.event_type);
    return [
      getDisplayName(ev),
      eventPersonLabel(ev) || '—',
      dir.label,
      ev.zone_name || ev.device_name || '—',
      ev.unit_name || ev.department_name || '—',
      formatTimestamp(ev.event_timestamp),
      ev.is_authorized === false ? 'Negado' : 'Autorizado',
    ];
  };

  const warnIfExportTruncated = (total: number) => {
    if (total > EXPORT_LIMIT) {
      toast(`Mostrando os ${EXPORT_LIMIT} eventos mais recentes de ${total.toLocaleString()} no período — estreite os filtros (ou o período) para exportar o restante.`, { icon: '', duration: 6000 });
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const { events: allEvents, total } = await fetchEventsForExport();
      warnIfExportTruncated(total);
      const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
      const headers = ['Nome', 'Tipo', 'Direção', 'Zona/Dispositivo', 'Unidade/Departamento', 'Data/Hora', 'Status'];
      const rows = allEvents.map(ev => buildExportRow(ev).map(v => escape(String(v))).join(','));
      const csv = ['﻿' + headers.map(escape).join(','), ...rows].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eventos_gerais_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Erro ao gerar CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const { events: allEvents, total } = await fetchEventsForExport();
      warnIfExportTruncated(total);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 14;

      try {
        const logoImg = new Image();
        logoImg.src = '/panther-logo.png';
        await new Promise<void>((resolve) => {
          logoImg.onload = () => { try { doc.addImage(logoImg, 'PNG', margin, 6, 20, 20); } catch {} resolve(); };
          logoImg.onerror = () => resolve();
          setTimeout(() => resolve(), 2000);
        });
      } catch {}

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('ACCESS', margin + 23, 20);
      doc.setLineWidth(0.5);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 30, pageW - margin, 30);

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Eventos Gerais', margin, 38);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const generatedAt = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
      doc.text(`Total: ${allEvents.length} evento${allEvents.length !== 1 ? 's' : ''}`, margin, 44);
      doc.text(`Gerado em: ${generatedAt}`, pageW - margin, 44, { align: 'right' });

      autoTable(doc, {
        startY: 50,
        head: [['Nome', 'Tipo', 'Direção', 'Zona/Dispositivo', 'Unidade/Departamento', 'Data/Hora', 'Status']],
        body: allEvents.map(buildExportRow),
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [17, 24, 39], textColor: 255, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 6) {
            data.cell.styles.textColor = data.cell.raw === 'Autorizado' ? [22, 101, 52] : [185, 28, 28];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 160);
        doc.text(`Página ${i} de ${totalPages}`, pageW - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        doc.text('Panther Access', margin, doc.internal.pageSize.getHeight() - 6);
      }

      doc.save(`eventos_gerais_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Erro ao gerar PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const fetchTimeSpent = useCallback(async () => {
    if (!customerId || zoneFilter === 'all') return;
    setTimeSpentLoading(true);
    try {
      const params = new URLSearchParams({ customer_id: customerId, zone_id: zoneFilter });
      if (typeFilter !== 'all') params.append('person_type', typeFilter);
      if (startDate) params.append('start_date', startDate.toISOString());
      if (endDate) { const e = new Date(endDate); e.setHours(23, 59, 59, 999); params.append('end_date', e.toISOString()); }
      const res = await fetchWithAuth(`/api/v1/events/time-spent?${params}`);
      if (res.ok) { const d = await res.json(); setTimeSpentData(d.data || []); }
    } catch {} finally { setTimeSpentLoading(false); }
  }, [customerId, zoneFilter, typeFilter, startDate, endDate]);

  useEffect(() => { setCurrentPage(1); }, [zoneFilter, directionFilter, typeFilter, debouncedSearch, startDate, endDate, unitFilter, departmentFilter]);
  useEffect(() => { fetchEvents(currentPage); }, [fetchEvents, currentPage]);

  useEffect(() => { fetchZones(); }, [fetchZones]);
  useEffect(() => { if (!embedded && activeTab === 'time-spent') fetchTimeSpent(); }, [fetchTimeSpent, activeTab, embedded]);

  const handleClick = async (event: AccessEvent) => {
    if (event.person_id) {
      openPersonDetail(event.person_id, false, embedded);
      return;
    }
    if (!event.vehicle_plate) return;
    // Resolve unit_id — try each source in order:
    // 1. Already in response (new enrichment)
    // 2. Fetch by vehicle_id (stored column)
    // 3. Search by plate (oldest events with no vehicle_id)
    let unitId = event.unit_id;
    if (!unitId && customerId) {
      try {
        const vehicleId = event.vehicle_id;
        const url = vehicleId
          ? `/api/v1/customers/${customerId}/vehicles/${vehicleId}`
          : `/api/v1/customers/${customerId}/vehicles?search=${encodeURIComponent(event.vehicle_plate)}`;
        const res = await fetchWithAuth(url);
        if (res.ok) {
          const data = await res.json();
          // GetVehicle wraps vehicle under data.data; ListVehicles uses data.vehicles[]
          const v = vehicleId ? data.data : (Array.isArray(data) ? data[0] : data.vehicles?.[0]);
          unitId = v?.unit_id ?? undefined;
        }
      } catch { /* ignore */ }
    }
    if (!unitId) return;
    if (onUnitClick) {
      onUnitClick(unitId, event.unit_name || '');
    } else {
      navigate(`/units?unit=${unitId}`);
    }
  };
  const hasActiveFilters = typeFilter !== 'all' || directionFilter !== 'all' || zoneFilter !== 'all' || !!debouncedSearch || !!startDate || !!endDate || insideOnly || departmentFilter !== 'all' || blockFilter !== 'all' || unitFilter !== 'all';
  const clearFilters = () => { setSearchTerm(''); setTypeFilter('all'); setDirectionFilter('all'); setZoneFilter('all'); setStartDate(null); setEndDate(null); setInsideOnly(false); setDepartmentFilter('all'); setBlockFilter('all'); setUnitFilter('all'); };

  const filteredTimeSpentData = insideOnly ? timeSpentData.filter(e => e.currently_inside) : timeSpentData;
  const totalPages = Math.ceil(totalEvents / PAGE_SIZE);
  const zoneOptions = [{ value: 'all', label: 'Todas as zonas' }, ...zones.map(z => ({ value: z.id, label: z.name }))];
  const departmentOptions = [{ value: 'all', label: 'Todos os departamentos' }, ...departments.map(d => ({ value: d.id, label: d.name }))];
  const blockOptions = [{ value: 'all', label: `Todos os ${blockLabel}s` }, ...blocks.map(b => ({ value: b.id, label: b.name }))];
  const unitOptions = [{ value: 'all', label: `Todas as ${unitLabel}s` }, ...units.map(u => ({ value: u.id, label: u.number }))];

  // This page's data comes from /api/v1/events/access and /api/v1/events/time-spent,
  // which the backend already 403s for porteiro-tier roles when the customer's
  // "Relatórios para Porteiro" setting is off — show a clean message instead of a
  // page that tries to load and silently fails.
  if (!canViewReports) {
    return embedded ? (
      <div className="flex flex-col h-full overflow-hidden bg-gray-950 text-gray-100">
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2.5 border-b border-gray-800">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-sm font-bold text-white tracking-wide">EVENTOS GERAIS</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
          <Activity className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm">Relatórios desabilitados para porteiro</p>
        </div>
      </div>
    ) : (
      <div className="p-6 text-center py-12">
        <Activity className="mx-auto h-12 w-12 text-gray-300" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Relatórios desabilitados</h3>
        <p className="mt-1 text-sm text-gray-500">
          O acesso a relatórios para porteiro está desabilitado para este cliente.
        </p>
      </div>
    );
  }

  // ── EMBEDDED (dark) layout ───────────────────────────────────────────────────
  if (embedded) {
    return (
      <div className="flex flex-col h-full overflow-hidden bg-gray-950 text-gray-100">
        {/* Compact header */}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2.5 border-b border-gray-800">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-sm font-bold text-white tracking-wide">EVENTOS GERAIS</span>
          {totalEvents > 0 && <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-medium">{totalEvents.toLocaleString()}</span>}
          <div className="flex-1" />
          <ReportButton
            dark
            loading={isExporting}
            disabled={totalEvents === 0}
            formats={[
              { key: 'pdf', label: 'Exportar PDF', onSelect: handleExportPDF },
              { key: 'csv', label: 'Exportar CSV', onSelect: handleExportCSV },
            ]}
          />
        </div>

        {/* Filter bar */}
        <div className="flex-shrink-0 bg-gray-900 border-b border-gray-800 p-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs text-gray-400 mb-1">Buscar pessoa</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                <input type="text" placeholder="Nome..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-7 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyan-500" />
                {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"><X className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
            <DarkSelect value={typeFilter} onChange={setTypeFilter} options={personTypeOptions} label="Tipo" />
            <DarkSelect value={zoneFilter} onChange={setZoneFilter} options={zoneOptions} label="Zona" />
            <DarkSelect value={directionFilter} onChange={setDirectionFilter} options={DIRECTION_OPTIONS} label="Direção" />
            {customerType === 'empresa' ? (
              <SearchableSelect dark value={departmentFilter} onChange={setDepartmentFilter} options={departmentOptions} placeholder="Todos os departamentos" label="Departamento" />
            ) : (
              <>
                <SearchableSelect dark value={blockFilter} onChange={setBlockFilter} options={blockOptions} placeholder={`Todos os ${blockLabel}s`} label={blockLabel} />
                <SearchableSelect dark value={unitFilter} onChange={setUnitFilter} options={unitOptions} placeholder={`Todas as ${unitLabel}s`} label={unitLabel} disabled={blockFilter === 'all'} />
              </>
            )}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Início</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none z-10" />
                <DatePicker selected={startDate} onChange={(d: Date | null) => setStartDate(d)} maxDate={endDate ?? undefined} locale="pt-BR" dateFormat="dd/MM/yy" placeholderText="Início" isClearable
                  className="w-[110px] pl-8 pr-2 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyan-500" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Fim</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none z-10" />
                <DatePicker selected={endDate} onChange={(d: Date | null) => setEndDate(d)} minDate={startDate ?? undefined} locale="pt-BR" dateFormat="dd/MM/yy" placeholderText="Fim" isClearable
                  className="w-[110px] pl-8 pr-2 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyan-500" />
              </div>
            </div>
            {hasActiveFilters && (
              <div><label className="block text-xs text-transparent mb-1">.</label>
                <button onClick={clearFilters} className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:border-gray-500 transition-colors">
                  <X className="h-4 w-4" />Limpar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Events */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 text-cyan-400 animate-spin" /><span className="ml-2 text-gray-400 text-sm">Carregando...</span></div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Activity className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm">{hasActiveFilters ? 'Nenhum evento com esses filtros' : 'Nenhum evento encontrado'}</p>
              {hasActiveFilters && <button onClick={clearFilters} className="mt-2 text-sm text-cyan-400 hover:text-cyan-300">Limpar filtros</button>}
            </div>
          ) : (
            <>
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {events.map(e => <DarkEventCard key={e.id} event={e} onClick={handleClick} />)}
              </div>
              <div>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalEvents} pageSize={PAGE_SIZE} onPageChange={setCurrentPage} isLoading={isLoading} dark />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── STANDALONE (light) layout ────────────────────────────────────────────────
  if (!customerId) {
    return (
      <div className="p-6 text-center py-12">
        <Building2 className="mx-auto h-12 w-12 text-gray-300" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum cliente selecionado</h3>
        <p className="mt-1 text-sm text-gray-500">Selecione um cliente para visualizar os eventos.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* Tab switcher — only shown in standalone when feature is enabled */}
      {timeSpentEnabled && (
        <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('events')}
            className={cn(
              'px-4 py-1.5 text-xs font-medium rounded-md transition-colors',
              activeTab === 'events' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Eventos
          </button>
          <button
            onClick={() => setActiveTab('time-spent')}
            className={cn(
              'px-4 py-1.5 text-xs font-medium rounded-md transition-colors',
              activeTab === 'time-spent' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            Tempo na Zona
          </button>
        </div>
      )}

      {/* Stats + active filter count — events tab only */}
      {activeTab === 'events' && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-mono text-gray-400">
            {isLoading ? 'CARREGANDO…' : `${totalEvents.toLocaleString()} ${totalEvents === 1 ? 'EVENTO' : 'EVENTOS'}`}
            {hasActiveFilters && ' · FILTROS ATIVOS'}
          </span>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <X className="h-3.5 w-3.5" /> Limpar filtros
              </button>
            )}
            <ReportButton
              loading={isExporting}
              disabled={totalEvents === 0}
              formats={[
                { key: 'pdf', label: 'Exportar PDF', onSelect: handleExportPDF },
                { key: 'csv', label: 'Exportar CSV', onSelect: handleExportCSV },
              ]}
            />
          </div>
        </div>
      )}

      {/* Filter row */}
      <div className="flex items-center gap-2 pb-3 border-b border-gray-200 flex-wrap">
        {activeTab === 'events' && (
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
        <LightSelect value={typeFilter} onChange={setTypeFilter} options={personTypeOptions} placeholder="Tipo" />
        <LightSelect value={zoneFilter} onChange={setZoneFilter} options={zoneOptions} placeholder="Zona" />
        {activeTab === 'events' && (
          <LightSelect value={directionFilter} onChange={setDirectionFilter} options={DIRECTION_OPTIONS} placeholder="Direção" />
        )}
        {customerType === 'empresa' ? (
          <SearchableSelect value={departmentFilter} onChange={setDepartmentFilter} options={departmentOptions} placeholder="Departamento" />
        ) : (
          <>
            <SearchableSelect value={blockFilter} onChange={setBlockFilter} options={blockOptions} placeholder={blockLabel} />
            <SearchableSelect value={unitFilter} onChange={setUnitFilter} options={unitOptions} placeholder={unitLabel} disabled={blockFilter === 'all'} />
          </>
        )}
        {activeTab === 'time-spent' && (
          <button
            onClick={() => setInsideOnly(v => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors',
              insideOnly
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
            )}
          >
            <span className={cn('h-1.5 w-1.5 rounded-full', insideOnly ? 'bg-green-500' : 'bg-gray-400')} />
            Dentro agora
          </button>
        )}
        <div className="relative">
          <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none z-10" />
          <DatePicker
            selected={startDate}
            onChange={(d: Date | null) => setStartDate(d)}
            maxDate={endDate ?? undefined}
            locale="pt-BR"
            dateFormat="dd/MM/yyyy"
            placeholderText="Data inicial"
            isClearable
            className="pl-8 pr-3 py-1.5 w-[130px] border border-gray-200 rounded-lg text-xs bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
          />
        </div>
        <div className="relative">
          <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none z-10" />
          <DatePicker
            selected={endDate}
            onChange={(d: Date | null) => setEndDate(d)}
            minDate={startDate ?? undefined}
            locale="pt-BR"
            dateFormat="dd/MM/yyyy"
            placeholderText="Data final"
            isClearable
            className="pl-8 pr-3 py-1.5 w-[130px] border border-gray-200 rounded-lg text-xs bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
          />
        </div>
      </div>

      {/* ── Events list ── */}
      {activeTab === 'events' && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-700 rounded-full animate-spin" />
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Activity className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">{hasActiveFilters ? 'Nenhum evento com esses filtros' : 'Nenhum evento registrado'}</p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline">Limpar filtros</button>
              )}
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-100">
                {events.map(e => <LightEventRow key={e.id} event={e} onClick={handleClick} />)}
              </ul>
              <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalEvents} pageSize={PAGE_SIZE} onPageChange={setCurrentPage} isLoading={isLoading} />
            </>
          )}
        </div>
      )}

      {/* ── Time Spent table ── */}
      {activeTab === 'time-spent' && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {zoneFilter === 'all' ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <MapPin className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">Selecione uma zona para ver o tempo de permanência</p>
            </div>
          ) : timeSpentLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-700 rounded-full animate-spin" />
            </div>
          ) : filteredTimeSpentData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Clock className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">{insideOnly ? 'Nenhuma pessoa dentro agora' : 'Nenhum dado de permanência encontrado'}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500 font-medium">
                  <th className="px-4 py-3 text-left">Pessoa</th>
                  <th className="px-4 py-3 text-center">Visitas</th>
                  <th className="px-4 py-3 text-center">Tempo Total</th>
                  <th className="px-4 py-3 text-center">Média/Visita</th>
                  <th className="px-4 py-3 text-center">Último Acesso</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTimeSpentData.map((entry, i) => {
                  const typeLabel = eventPersonLabel(entry);
                  const clickable = !!entry.person_id;
                  const lastAccess = entry.last_exit ?? entry.last_entry;
                  return (
                    <tr
                      key={entry.person_id ?? i}
                      onClick={() => { if (clickable && entry.person_id) openPersonDetail(entry.person_id); }}
                      className={cn('transition-colors', clickable ? 'cursor-pointer hover:bg-gray-50' : '')}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-[26px] h-8 rounded-md overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 flex items-center justify-center">
                            {entry.face_photo_url
                              ? <img src={getAbsoluteImageUrl(entry.face_photo_url)} alt="" className="h-full w-full object-cover" />
                              : <User className="h-4 w-4 text-gray-400" />
                            }
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{entry.person_name || '—'}</p>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              {typeLabel && <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded border border-gray-200">{typeLabel}</span>}
                              {entry.unit_name && <span className="text-[10px] text-gray-400">{entry.unit_name}</span>}
                              {entry.department_name && !entry.unit_name && <span className="text-[10px] text-gray-400">{entry.department_name}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700 font-medium">{entry.visits_count}</td>
                      <td className="px-4 py-3 text-center font-medium text-gray-900">{formatDuration(entry.total_seconds)}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{entry.visits_count > 0 ? formatDuration(entry.avg_seconds_per_visit) : '—'}</td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs">{lastAccess ? formatTimestamp(lastAccess) : '—'}</td>
                      <td className="px-4 py-3 text-center">
                        {entry.currently_inside
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700"><span className="h-1.5 w-1.5 rounded-full bg-green-500" />Dentro agora</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">Fora</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default EventosGerais;
